/* global globalThis */
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter } from "react-router-dom";
import { CssBaseline } from "@mui/material";

import App from "./App";
import store, { persistor } from "./redux/store";
import reportWebVitals from "./reportWebVitals";

import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./redux/authConfig";

import "./index.css";

const msalInstance = new PublicClientApplication(msalConfig);

const initializeMsal = async () => {
  await msalInstance.initialize();

  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event?.payload?.account
    ) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
    }
  });
};

initializeMsal().catch((error) => {
  console.error("MSAL initialization failed", error);
});

const root = ReactDOM.createRoot(document.getElementById("root"));
const BASENAME = process.env.REACT_APP_BASENAME || "/";

if (
  BASENAME &&
  !globalThis.location.pathname.startsWith(BASENAME) &&
  globalThis.location.pathname !== "/"
) {
  try {
    const currentUrl = new URL(globalThis.location.href);

    // Construct new pathname with BASENAME, avoiding double slashes
    let newPath = BASENAME + currentUrl.pathname;
    newPath = newPath.replaceAll(/\/{2,}/g, "/");

    // Use URL object to construct the target, explicitly setting the base to the current origin
    const targetUrl = new URL(newPath, globalThis.location.origin);
    targetUrl.search = currentUrl.search;
    targetUrl.hash = currentUrl.hash;

    // Final sanity check
    if (targetUrl.origin === globalThis.location.origin) {
      globalThis.location.replace(targetUrl.href);
    }
  } catch (e) {
    console.warn("Invalid URL constructed, skipping redirection:", e);
  }
}

root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <BrowserRouter basename={BASENAME}>
        <CssBaseline />
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </BrowserRouter>
    </PersistGate>
  </Provider>,
);

reportWebVitals();

(() => {
  const rootEl = document.getElementById("root");
  if (!rootEl) return;

  const focusableSelector = [
    "a[href]",
    "button",
    "input",
    "select",
    "textarea",
    "[tabindex]",
    '[role="button"]',
    '[role="link"]',
    '[role="textbox"]',
  ].join(",");

  const disableDescendants = () => {
    if ("inert" in rootEl) {
      rootEl.inert = true;
      return;
    }

    for (const el of rootEl.querySelectorAll(focusableSelector)) {
      if (el.closest('[aria-hidden="true"]') !== rootEl) continue;

      if (el.dataset.prevTabindex === undefined) {
        const hadExplicit = el.matches("[tabindex]");
        el.dataset.prevTabindex = hadExplicit ? String(el.tabIndex) : "";
        el.dataset.prevTabindexHadAttr = hadExplicit ? "1" : "0";
      }

      el.tabIndex = -1;
    }
  };

  const restoreDescendants = () => {
    if ("inert" in rootEl) {
      rootEl.inert = false;
      return;
    }
    for (const el of rootEl.querySelectorAll("[data-prev-tabindex]")) {
      const prev = el.dataset.prevTabindex;
      const hadAttr = el.dataset.prevTabindexHadAttr === "1";
      if (!hadAttr) {
        el.tabIndex = 0;
      } else if (prev !== "") {
        const numeric = Number.parseInt(prev, 10);
        if (Number.isNaN(numeric)) {
          el.setAttribute("tabindex", prev);
        } else {
          el.tabIndex = numeric;
        }
      }
      delete el.dataset.prevTabindex;
      delete el.dataset.prevTabindexHadAttr;
    }
  };

  const syncState = () => {
    if (rootEl.getAttribute("aria-hidden") === "true") {
      disableDescendants();
    } else {
      restoreDescendants();
    }
  };

  const observer = new MutationObserver(syncState);
  observer.observe(rootEl, {
    attributes: true,
    attributeFilter: ["aria-hidden"],
  });
  syncState();
})();

// Back from the Microsoft accounts page restores the cached app page (bfcache);
// if the user is logged out, go straight to landing and drop that history entry.
globalThis.addEventListener("pageshow", (event) => {
  if (event.persisted && msalInstance.getAllAccounts().length === 0) {
    // Cover the stale restored page with a loader until landing loads
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:2147483647;background:#fff;display:flex;align-items:center;justify-content:center;";
    overlay.innerHTML =
      '<div style="width:44px;height:44px;border:4px solid #e0e0e0;border-top-color:#1976d2;border-radius:50%;animation:lucy-spin 0.8s linear infinite"></div><style>@keyframes lucy-spin{to{transform:rotate(360deg)}}</style>';
    document.body.appendChild(overlay);
    globalThis.location.replace("/landing");
  }
});

(() => {
  const updateScreenClass = () => {
    if (globalThis === undefined || document === undefined) return;
    const isLarge = window.innerWidth >= 2310;
    document.body.classList.toggle("large-screen", isLarge);
  };
  updateScreenClass();
  globalThis.addEventListener("resize", updateScreenClass);
})();
